import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useAuth } from '@/services/auth';
import { masqReadOnlyStore } from '@/utils/masq';

const MASQ_READ_ONLY_SNACKBAR_ID = 'route.masqReadOnly';
const MASQ_READ_ONLY_AUTO_CLOSE_MS = 10_000;
const MASQ_READ_ONLY_MESSAGE = "You're in read-only masquerading mode, so you can't view that page.";

type RedirectIfMasqReadOnlyProps = {
  redirectTo: string;
  children: React.ReactNode;
};

/**
 * Turns a read-only masquerade session away from a route entirely (with the
 * alert Ember's `redirectIfMasqingReadOnly` showed) instead of rendering it
 * read-only. For routes whose only purpose is editing, where a read-only view
 * would be an empty promise.
 */
export function RedirectIfMasqReadOnly({ redirectTo, children }: RedirectIfMasqReadOnlyProps) {
  const auth = useAuth();
  const masqReadOnly = useStore(masqReadOnlyStore);
  const snackbars = useSnackbars();
  const hasShownSnackbarRef = useRef(false);

  const isDenied = auth.account?.is_masq === true && masqReadOnly;

  useEffect(() => {
    if (isDenied) {
      if (!hasShownSnackbarRef.current) {
        snackbars.create({
          message: MASQ_READ_ONLY_MESSAGE,
          variant: 'critical',
          id: MASQ_READ_ONLY_SNACKBAR_ID,
          autoCloseTimeout: MASQ_READ_ONLY_AUTO_CLOSE_MS,
        });
        hasShownSnackbarRef.current = true;
      }
    } else {
      hasShownSnackbarRef.current = false;
    }
    // oxlint-disable-next-line react/exhaustive-deps -- re-show the alert only when the denial flips; the snackbar factory identity is deliberately excluded
  }, [isDenied]);

  if (isDenied) {
    return <SafeNavigate to={redirectTo} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children}</>;
}
